// ---------------------------------------------
// theme-header.js
// ---------------------------------------------

if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

/**
 *  @class
 *  @function ThemeHeader
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu           = this.querySelector('#mobile-menu');
      this.toggle         = this.querySelector('.mobile-toggle-wrapper');

      // Escape key closes mobile menu
      document.addEventListener('keyup', (e) => {
        if (e.code && e.code.toUpperCase() === 'ESCAPE') {
          this.toggle.removeAttribute('open');
          this.toggle.classList.remove('active');
        }
      });

      // If theme configured as sticky by default
      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
      }

      // Mobile toggle click
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => this.toggle.classList.add('active'));
        }
      });

      // Defer all layout-heavy header logic off the critical path
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this._deferredHeaderSetup());
      } else {
        window.addEventListener('load', () => this._deferredHeaderSetup());
      }
    }

    /**
     * Offload any getBoundingClientRect / clientHeight usage
     * and only attach scroll listeners after LCP / idle time.
     */
    _deferredHeaderSetup() {
      // Initial CSS variable updates
      if (document.querySelector('.announcement-bar-section')) {
        this.setAnnouncementHeight();
      }
      this.setHeaderOffset();
      this.setHeaderHeight();

      // Now wire up scroll listeners
      window.addEventListener('scroll', this.setStickyClass.bind(this),      { passive: true });
      window.addEventListener('scroll', this.setHeaderOffset.bind(this),     { passive: true });
      window.addEventListener('scroll', this.setHeaderHeight.bind(this),     { passive: true });
      if (document.querySelector('.announcement-bar-section')) {
        window.addEventListener('scroll', this.setAnnouncementHeight.bind(this), { passive: true });
      }

      // Kick off one resize/scroll so CSS vars are applied immediately
      window.dispatchEvent(new Event('scroll'));
      window.dispatchEvent(new Event('resize'));
    }

    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        const offset = parseInt(this.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
        this.classList.toggle('is-sticky', window.scrollY >= offset && window.scrollY > 0);
      }
    }

    setAnnouncementHeight() {
      const a_bar = document.querySelector('.announcement-bar-section');
      const h     = a_bar.clientHeight;
      document.documentElement.style.setProperty('--announcement-height', h + 'px');
    }

    setHeaderOffset() {
      const h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }

    setHeaderHeight() {
      const h = this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }

    onSummaryClick(event) {
      const summaryElement   = event.currentTarget;
      const detailsElement   = summaryElement.parentNode;
      const parentMenu       = detailsElement.closest('.link-container');
      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenu && parentMenu.classList.add('submenu-open');
      }, 100);
    }

    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }

    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }

    closeAnimation(detailsElement) {
      let animationStart;
      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }
        const elapsedTime = time - animationStart;
        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };
      window.requestAnimationFrame(handleAnimation);
    }
  }

  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
      this.submenus = this.querySelectorAll(
        '.thb-full-menu > .menu-item-has-children:not(.menu-item-has-megamenu) > .sub-menu'
      );
    }

    connectedCallback() {
      if (!this.submenus.length) return;
      const _this = this;
      document.fonts.ready.then(() => {
        window.addEventListener('resize', debounce(() => {
          _this.resizeSubMenus();
        }, 100));
      });
    }

    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        const subSubmenus = submenu.querySelectorAll(
          ':scope > .menu-item-has-children > .sub-menu'
        );
        subSubmenus.forEach((subSubmenu) => {
          const rect   = subSubmenu.getBoundingClientRect();
          const overflow = rect.left + rect.width > window.innerWidth;
          subSubmenu.parentElement.classList.toggle('left-submenu', overflow);
        });
      });
    }
  }

  customElements.define('full-menu', FullMenu);
}